import { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { useQueryClient } from 'react-query';
import { usePlaylistDetail } from './usePlaylistDetail';
import {
  playUrlForPlaylistItem,
  withPlaylistQueryParams,
} from './playlistPaths';
import PlaylistsApi from './api';
import type { PlaylistContentType } from './api';

const DELAY_MS = 5000;

export function PlaylistPlaybackBar({
  sessionContentId,
  sessionContentType,
  advanceSignal,
}: {
  sessionContentId: string;
  sessionContentType: PlaylistContentType;
  advanceSignal: number;
}) {
  const history = useHistory();
  const location = useLocation();
  const qc = useQueryClient();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const playlistId = params.get('playlistId');
  const playlistItemId = params.get('playlistItemId');
  const { data: playlistDetail } = usePlaylistDetail(playlistId ?? undefined, !!playlistId);

  const sorted = useMemo(() => {
    if (!playlistDetail?.items) return [];
    return [...playlistDetail.items].sort((a, b) => a.position - b.position);
  }, [playlistDetail]);

  const currentIndex = useMemo(() => {
    if (!sorted.length) return -1;
    if (playlistItemId) {
      const by = sorted.findIndex((i) => i.id === playlistItemId);
      if (by >= 0) return by;
    }
    return sorted.findIndex(
      (i) =>
        i.contentId === sessionContentId &&
        i.contentType === sessionContentType,
    );
  }, [sorted, playlistItemId, sessionContentId, sessionContentType]);

  const lastProgressKey = useRef<string | null>(null);

  useEffect(() => {
    if (!playlistId || !playlistItemId) return;
    const key = `${playlistId}:${playlistItemId}`;
    if (lastProgressKey.current === key) return;
    lastProgressKey.current = key;
    void PlaylistsApi.updateProgress(playlistId, {
      lastPlayedItemId: playlistItemId,
    });
  }, [playlistId, playlistItemId]);

  const goToIndex = useCallback(
    async (idx: number, completeCurrentId?: string) => {
      if (!playlistId) return;
      const next = sorted[idx];
      const payload: Parameters<typeof PlaylistsApi.updateProgress>[1] = {};
      if (completeCurrentId) payload.completedItemIds = [completeCurrentId];
      if (next) payload.lastPlayedItemId = next.id;
      await PlaylistsApi.updateProgress(playlistId, payload);
      void qc.invalidateQueries(['playlist', playlistId]);
      if (!next) {
        const pathOnly = location.pathname.split('?')[0] ?? '/';
        history.replace(pathOnly);
        return;
      }
      history.push(
        withPlaylistQueryParams(
          playUrlForPlaylistItem(next),
          playlistId,
          next.id,
        ),
      );
    },
    [playlistId, sorted, history, location.pathname, qc],
  );

  const skip = useCallback(() => {
    const cur = sorted[currentIndex];
    if (currentIndex < 0 || !cur) return;
    void goToIndex(currentIndex + 1, cur.id);
  }, [currentIndex, sorted, goToIndex]);

  const hasNext = currentIndex >= 0 && currentIndex < sorted.length - 1;

  // The countdown is driven by advanceSignal alone. Reading the rest through
  // refs keeps a playlist refetch or a re-created callback from restarting it
  // (or reviving it after the user cancelled).
  const hasNextRef = useRef(hasNext);
  hasNextRef.current = hasNext;
  const skipRef = useRef(skip);
  skipRef.current = skip;

  const [secondsLeft, setSecondsLeft] = useState(0);
  const countdownRef = useRef<number>();

  const cancelCountdown = useCallback(() => {
    window.clearInterval(countdownRef.current);
    countdownRef.current = undefined;
    setSecondsLeft(0);
  }, []);

  useEffect(() => {
    if (!advanceSignal || !hasNextRef.current) return;
    let left = Math.ceil(DELAY_MS / 1000);
    setSecondsLeft(left);
    countdownRef.current = window.setInterval(() => {
      left -= 1;
      setSecondsLeft(left);
      if (left > 0) return;
      window.clearInterval(countdownRef.current);
      countdownRef.current = undefined;
      skipRef.current();
    }, 1000);
    return () => window.clearInterval(countdownRef.current);
  }, [advanceSignal]);

  useEffect(() => {
    cancelCountdown();
  }, [playlistItemId, cancelCountdown]);

  const goToNext = useCallback(() => {
    cancelCountdown();
    skip();
  }, [cancelCountdown, skip]);

  if (!playlistId || sorted.length <= 1) return null;

  const isCountingDown = hasNext && secondsLeft > 0;

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        right: 16,
        zIndex: 1200,
        p: 2,
        maxWidth: 560,
        mx: 'auto',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Typography variant="body2">
          {isCountingDown
            ? `Następna sesja za ${secondsLeft}s`
            : `Playlista${playlistDetail?.name ? `: ${playlistDetail.name}` : ''}`}
        </Typography>
        <Stack direction="row" gap={1}>
          {isCountingDown && (
            <Button size="small" variant="outlined" onClick={cancelCountdown}>
              Anuluj
            </Button>
          )}
          {hasNext && (
            <Button size="small" variant="contained" onClick={goToNext}>
              Następna
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
